import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
export function ProgramPaths() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="programs" className="py-20 md:py-28 bg-warm-50" data-id="element-371">
      <div className="max-w-7xl mx-auto px-4 sm:px-6" ref={ref} data-id="element-372">
        {/* Section header */}
        <motion.div className="text-center max-w-4xl mx-auto mb-14 md:mb-16" initial={{
        opacity: 0,
        y: 24
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-373">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-navy-950 font-heading tracking-tight" data-id="element-374">
            Programs for Every Member of the Family
          </h2>
          <p className="mt-4 text-lg text-ink-muted leading-relaxed" data-id="element-375">
            From first kicks to black belts — classes built for every age, every
            skill level, and every goal.
          </p>
        </motion.div>

        {/* Program cards — 3-col grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-376">
          {/* Early Skillz */}
          <motion.a href="#cta" className="group relative bg-white border border-warm-200 rounded-2xl overflow-hidden transition-shadow duration-300 hover:shadow-xl hover:shadow-navy-950/5 focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 flex flex-col" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-377">
            <img src="https://cdn.magicpatterns.com/uploads/9bLVjKcz1pvZooQTv3ckxx/Early-Skills2.webp" alt="Two young kids hugging at the martial arts academy" className="w-full h-48 object-cover" loading="lazy" data-id="element-378" />
            <div className="p-6 md:p-8 flex-1 flex flex-col" data-id="element-379">
              <h3 className="text-xl font-bold text-navy-950 font-heading" data-id="element-380">
                Early Skillz
              </h3>
              <p className="text-sm font-bold text-amber-dark mt-1 uppercase tracking-wide" data-id="element-381">
                Preschool · Ages 3–4
              </p>
              <p className="mt-3 text-base text-ink-muted leading-relaxed flex-1" data-id="element-382">
                Build motor skills, coordination, and early confidence through
                age-appropriate martial arts fundamentals in a fun, nurturing
                setting.
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-navy-950 group-hover:text-amber-dark transition-colors" data-id="element-383">
                Learn More
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-384" />
              </span>
            </div>
          </motion.a>

          {/* Basic Skillz */}
          <motion.a href="#cta" className="group relative bg-white border border-warm-200 rounded-2xl overflow-hidden transition-shadow duration-300 hover:shadow-xl hover:shadow-navy-950/5 focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 flex flex-col" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.15
        }} data-id="element-385">
            <img src="https://cdn.magicpatterns.com/uploads/a8eQQ2uFmk8FKE69bgojSZ/Basic-Skills2.webp" alt="Kids giving thumbs up at the martial arts academy" className="w-full h-48 object-cover" loading="lazy" data-id="element-386" />
            <div className="p-6 md:p-8 flex-1 flex flex-col" data-id="element-387">
              <h3 className="text-xl font-bold text-navy-950 font-heading" data-id="element-388">
                Basic Skillz
              </h3>
              <p className="text-sm font-bold text-amber-dark mt-1 uppercase tracking-wide" data-id="element-389">
                Kids · Ages 5–6
              </p>
              <p className="mt-3 text-base text-ink-muted leading-relaxed flex-1" data-id="element-390">
                Develop focus, discipline, and foundational Taekwondo skills in
                a structured, encouraging environment that kids love coming back
                to.
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-navy-950 group-hover:text-amber-dark transition-colors" data-id="element-391">
                Learn More
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-392" />
              </span>
            </div>
          </motion.a>

          {/* Core Skillz */}
          <motion.a href="#cta" className="group relative bg-white border border-warm-200 rounded-2xl overflow-hidden transition-shadow duration-300 hover:shadow-xl hover:shadow-navy-950/5 focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 flex flex-col" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.2
        }} data-id="element-393">
            <img src="https://cdn.magicpatterns.com/uploads/93VVPh1bDt2hsEjk1zfsbK/Core-Skills.webp" alt="Four young girls in martial arts uniforms smiling" className="w-full h-48 object-cover object-top" loading="lazy" data-id="element-394" />
            <div className="p-6 md:p-8 flex-1 flex flex-col" data-id="element-395">
              <h3 className="text-xl font-bold text-navy-950 font-heading" data-id="element-396">
                Core Skillz
              </h3>
              <p className="text-sm font-bold text-amber-dark mt-1 uppercase tracking-wide" data-id="element-397">
                Kids · Ages 7–9
              </p>
              <p className="mt-3 text-base text-ink-muted leading-relaxed flex-1" data-id="element-398">
                Strengthen respect, self-defense awareness, and leadership
                through more advanced Taekwondo training and character
                development.
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-navy-950 group-hover:text-amber-dark transition-colors" data-id="element-399">
                Learn More
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-400" />
              </span>
            </div>
          </motion.a>

          {/* Teen & Adult */}
          <motion.a href="#cta" className="group relative bg-white border border-warm-200 rounded-2xl overflow-hidden transition-shadow duration-300 hover:shadow-xl hover:shadow-navy-950/5 focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 flex flex-col" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.25
        }} data-id="element-401">
            <img src="https://cdn.magicpatterns.com/uploads/55N4Qm6oDuG7uYsscphVf1/teen-adult-martial-arts.webp" alt="Adult martial artist performing a flying kick to break boards" className="w-full h-48 object-cover" loading="lazy" data-id="element-402" />
            <div className="p-6 md:p-8 flex-1 flex flex-col" data-id="element-403">
              <h3 className="text-xl font-bold text-navy-950 font-heading" data-id="element-404">
                Teen &amp; Adult
              </h3>
              <p className="text-sm font-bold text-amber-dark mt-1 uppercase tracking-wide" data-id="element-405">
                Ages 10+
              </p>
              <p className="mt-3 text-base text-ink-muted leading-relaxed flex-1" data-id="element-406">
                Challenge yourself with real fitness, practical self-defense,
                and a supportive training community that pushes you to grow.
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-navy-950 group-hover:text-amber-dark transition-colors" data-id="element-407">
                Learn More
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-408" />
              </span>
            </div>
          </motion.a>

          {/* Spectrum Skillz */}
          <motion.a href="#cta" className="group relative bg-white border border-warm-200 rounded-2xl overflow-hidden transition-shadow duration-300 hover:shadow-xl hover:shadow-navy-950/5 focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 flex flex-col" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.3
        }} data-id="element-409">
            <img src="https://cdn.magicpatterns.com/uploads/5kmvnaFrTAksebRpPNcekX/Spectrum-Skills.webp" alt="Group of diverse students holding broken boards" className="w-full h-48 object-cover" loading="lazy" data-id="element-410" />
            <div className="p-6 md:p-8 flex-1 flex flex-col" data-id="element-411">
              <h3 className="text-xl font-bold text-navy-950 font-heading" data-id="element-412">
                Spectrum Skillz
              </h3>
              <p className="text-sm font-bold text-amber-dark mt-1 uppercase tracking-wide" data-id="element-413">
                Specialized Program
              </p>
              <p className="mt-3 text-base text-ink-muted leading-relaxed flex-1" data-id="element-414">
                A specialized martial arts program for neurodiverse kids, built
                on patience, structure, and celebration of every milestone.
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-navy-950 group-hover:text-amber-dark transition-colors" data-id="element-415">
                Learn More
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-416" />
              </span>
            </div>
          </motion.a>

          {/* Birthday Parties */}
          <motion.a href="/birthday-parties" className="group relative bg-white border border-warm-200 rounded-2xl overflow-hidden transition-shadow duration-300 hover:shadow-xl hover:shadow-navy-950/5 focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 flex flex-col" initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.35
        }} data-id="element-417">
            <img src="https://cdn.magicpatterns.com/uploads/7CS9AvAn9jTTn6CEJ1L7EH/Birthday-Parties.webp" alt="Kids eating pizza at a birthday party" className="w-full h-48 object-cover" loading="lazy" data-id="element-418" />
            <div className="p-6 md:p-8 flex-1 flex flex-col" data-id="element-419">
              <h3 className="text-xl font-bold text-navy-950 font-heading" data-id="element-420">
                Birthday Parties
              </h3>
              <p className="text-sm font-bold text-amber-dark mt-1 uppercase tracking-wide" data-id="element-421">
                Ages 4+
              </p>
              <p className="mt-3 text-base text-ink-muted leading-relaxed flex-1" data-id="element-422">
                Give your child an unforgettable martial arts birthday party.
                Action-packed, instructor-led, and totally hassle-free.
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-navy-950 group-hover:text-amber-dark transition-colors" data-id="element-423">
                Learn More
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-424" />
              </span>
            </div>
          </motion.a>
        </div>
      </div>
    </section>;
}